import { Button, Icon } from '@aidkitorg/component-library';
import type { IconName } from '@aidkitorg/component-library/src/Icon';
import { useHistory } from 'react-router-dom';
import { useTheme } from '../Contexts/ThemeContext';
import { useLocalizedStrings } from '../Localization';

type ErrorPageLayoutProps = {
  iconName: IconName;
  iconType?: 'solid' | 'outline';
  title: string;
  description: React.ReactNode;
};

const ErrorPageLayout = ({
  iconName,
  title,
  description,
}: ErrorPageLayoutProps) => {
  const { logo } = useTheme();
  const history = useHistory();
  const L = useLocalizedStrings();
  const strings = L.admin_panel.error_pages;

  return (
    <div className="fixed w-full min-h-screen flex flex-col z-[999] bg-white">
      <div className="flex-grow flex-col flex items-center justify-center px-4 pb-16">
        <div className="pt-8 pb-4 flex justify-center">
          <div className="w-32">
            {logo}
          </div>
        </div>

        <div className="rounded-2xl shadow-xl border p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-2">
            <div className="w-16 h-16 flex items-center justify-center">
              <Icon
                type="solid"
                name={iconName}
                className="w-full h-full text-[var(--ak-identity-color-gray)]"
              />
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-2">
            {title}
          </h2>

          <div className="text-[var(--ak-identity-color-gray)] mb-4">
            {description}
          </div>

          <div className="flex flex-col justify-center gap-3">
            <Button
              variant="primary"
              onClick={() => history.push('/admin-panel/home')}
            >
              {strings.buttons.go_home}
            </Button>
            <Button
              variant="secondary"
              icon={<Icon name="ArrowPathIcon" className="w-5 h-5" />}
              onClick={() => window.location.reload()}
            >
              {strings.buttons.retry}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotFoundPage = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.error_pages;

  return (
    <ErrorPageLayout
      iconName="DocumentMagnifyingGlassIcon"
      title={strings.not_found_page.title}
      description={strings.not_found_page.description}
    />
  );
};

export const ForbiddenPage = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.error_pages;

  return (
    <ErrorPageLayout
      iconName="ShieldExclamationIcon"
      title={strings.forbidden_page.title}
      description={strings.forbidden_page.description}
    />
  );
};

export const ServerErrorPage = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.error_pages;

  return (
    <ErrorPageLayout
      iconName="ExclamationTriangleIcon"
      title={strings.server_error_page.title}
      description={
        <>
          <p className="mb-2">
            {strings.server_error_page.description}
          </p>
          <p>
            {strings.server_error_page.description_part_two}{' '}
            <Button
              variant="link"
              href="https://community.aidkit.cloud"
              openInNewWindow
              className="font-medium inline"
              size="lg"
            >
              community.aidkit.cloud
            </Button>
          </p>
        </>
      }
    />
  );
};

export const OfflinePage = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.error_pages;

  return (
    <ErrorPageLayout
      iconName="SignalSlashIcon"
      title={strings.offline_page.title}
      description={strings.offline_page.description}
    />
  );
};
